import { ConvertDateTime } from "../../../utils/TimeUtils";
import type { UserProfileInterface } from "../../account_settings/ts";
import { MessagesStatuses, MessagesStatusesEnum } from "../constants";
import type {
  ConversationMessagesRequest,
  SelectedConversation,
  CreateConversationMessage,
  ConversationDetail,
  ConversationData,
  PresignUrlRequest,
  UploadFileItem,
  SerializedConversationPayload,
} from "../ts";

export const serializeConversationPayload = (
  data: ConversationMessagesRequest
): SerializedConversationPayload=> {
  return {
    messages: data.messages.map((mes) => ({
      id: mes.id,
      message: mes.message,
      reply_to: mes.reply_to,

      files: mes.files?.map((file) => ({
        key: file.file_key,
        name: file.file.name,
        size: file.file.size,
        type: file.file.type,
      })),
    })),
  };
};

export const serializePresignUrlPyalod = async(data: PresignUrlRequest): Promise<FormData> => {
  const formData = new FormData();
  formData.append("file_name", data.file_name as string);
  formData.append("file_type", data.file_type as string);
  return formData;
}

export const CreateMessageSerializer = (
  message: string,
  files: UploadFileItem[],
  conversation: SelectedConversation,
  reply_to?: string | null
): ConversationMessagesRequest => {
  const messageAuthor =
    conversation.conversation?.client?.profile_handle ===
    conversation?.companion?.profile_handle
      ? conversation.conversation?.provider
      : conversation.conversation?.client;
  const payload = {
    conversation_id: conversation.conversation.id,
    messages: [
      {
        created_at: new Date().toString(),
        message: message,
        author: messageAuthor,
        is_system_message: false,
        status: "Undelivered",
        files,
        reply_to,
      } as CreateConversationMessage,
    ],
    message_id_list: []
  };
  return payload;
};

export const UpdateMessageSerializer = (
  message: string,
  conversation: SelectedConversation,
  message_id: string
): ConversationMessagesRequest => {
  const messageAuthor =
    conversation.conversation?.client?.profile_handle ===
    conversation?.companion?.profile_handle
      ? conversation.conversation?.provider
      : conversation.conversation?.client;
  const payload = {
    conversation_id: conversation.conversation.id,
    messages: [
      {
        message: message,
        author: messageAuthor,
        is_system_message: false,
        id: message_id,
      } as CreateConversationMessage,
    ],
  };
  return payload as ConversationMessagesRequest;
};

export const SerializeConversation = (
  conv: ConversationDetail,
  user_profile: UserProfileInterface | null
): ConversationData => {
  const companion =
    conv.client?.profile_handle === user_profile?.profile_handle
      ? conv.provider
      : conv.client;
  const lastMessageStatus = MessagesStatuses.find(
    (mesStatus) => mesStatus.index === conv.last_message?.status
  )?.status;
  const lastMessageTime = conv.last_message?.created_at
    ? ConvertDateTime(conv.last_message.created_at)
    : undefined;
  const isLastMessageCompanions =
    conv.last_message?.author?.profile_handle === companion?.profile_handle;
  const isMessageDelivered =
    conv.last_message?.author &&
    !isLastMessageCompanions &&
    (lastMessageStatus === MessagesStatusesEnum.sent ||
      lastMessageStatus === MessagesStatusesEnum.read);

  return {
    id: conv.id,
    profile_image: companion?.profile_image,
    name: companion?.name,
    lastMessage: conv.last_message?.message,
    lastMessageTime: lastMessageTime,
    lastMessageIsSystem: !conv.last_message?.author,
    isOnline: companion?.is_online,
    isDelivered: isMessageDelivered,
    isNew:
      lastMessageStatus === MessagesStatusesEnum.sent &&
      isLastMessageCompanions,
    lastMessageCompanions: isLastMessageCompanions,
    unreadCount: conv.unread_messages_count,
    isTyping: companion?.is_typing ?? false,
    isSeen:
      !isLastMessageCompanions &&
      lastMessageStatus === MessagesStatusesEnum.read,
    isSending: lastMessageStatus === MessagesStatusesEnum.undelivered,
  };
};
